import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, Clock, MapPin, DollarSign, User, FileText, 
  Star, AlertCircle, CheckCircle, Send, ArrowLeft 
} from 'lucide-react';
import { useCustomerAuth, customerApi } from '../../contexts/CustomerAuthContext';
import CustomerHeader from './CustomerHeader';
import Footer from '../public/Footer';
import './CustomerDashboard.css';

interface Category {
  id: number;
  name: string;
  description: string;
  icon: string;
  skills: Skill[];
}

interface Skill {
  id: number;
  name: string;
  description: string;
}

interface Worker {
  id: number;
  first_name: string;
  last_name: string;
  profile_picture: string;
  bio: string;
  hourly_rate: number;
  rating: number;
  total_reviews: number;
  parish: string;
  skills: string;
}

const ServiceInquiry: React.FC = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryId: '',
    skillId: '',
    locationAddress: '',
    scheduledDate: '',
    scheduledTime: '',
    budgetMin: '',
    budgetMax: '',
    priority: 'medium',
    clientNotes: '',
  });
  const [categories, setCategories] = useState<Category[]>([]);
  const [availableWorkers, setAvailableWorkers] = useState<Worker[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [currentStep, setCurrentStep] = useState(1);

  const { customer, isAuthenticated } = useCustomerAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (formData.categoryId) {
      fetchAvailableWorkers();
    }
  }, [formData.categoryId, formData.skillId]);

  const fetchCategories = async () => {
    try {
      const response = await customerApi.get('/customers/categories');
      if (response.data.success) {
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchAvailableWorkers = async () => {
    try {
      const params = new URLSearchParams();
      if (formData.categoryId) params.append('categoryId', formData.categoryId);
      if (formData.skillId) params.append('skillId', formData.skillId);
      if (customer?.parish) params.append('parish', customer.parish);

      const response = await customerApi.get(`/customers/available-workers?${params}`);
      if (response.data.success) {
        setAvailableWorkers(response.data.workers || []);
      }
    } catch (error) {
      console.error('Failed to fetch workers:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const validateStep1 = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Service title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Service description is required';
    }

    if (!formData.categoryId) {
      newErrors.categoryId = 'Please select a category';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.locationAddress.trim()) {
      newErrors.locationAddress = 'Location is required';
    }

    if (!formData.scheduledDate) {
      newErrors.scheduledDate = 'Date is required';
    } else {
      const selectedDate = new Date(formData.scheduledDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.scheduledDate = 'Date cannot be in the past';
      }
    }

    if (!formData.scheduledTime) {
      newErrors.scheduledTime = 'Time is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validateStep2()) {
      setCurrentStep(3);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep2()) return;

    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await customerApi.post('/customers/service-inquiry', {
        title: formData.title,
        description: formData.description,
        categoryId: parseInt(formData.categoryId),
        skillId: formData.skillId ? parseInt(formData.skillId) : undefined,
        locationAddress: formData.locationAddress,
        scheduledDate: formData.scheduledDate,
        scheduledTime: formData.scheduledTime,
        budgetMin: formData.budgetMin ? parseFloat(formData.budgetMin) : undefined,
        budgetMax: formData.budgetMax ? parseFloat(formData.budgetMax) : undefined,
        priority: formData.priority,
        clientNotes: formData.clientNotes || undefined,
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Service inquiry submitted successfully!' });
        setTimeout(() => {
          navigate('/customer/dashboard');
        }, 2000);
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to submit inquiry' });
      }
    } catch (error: any) {
      console.error('Service inquiry error:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to submit inquiry' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const selectedCategory = categories.find(cat => cat.id === parseInt(formData.categoryId));

  return (
    <>
      <CustomerHeader />
      <div className="service-inquiry-container">
        <div className="container">
          <div className="inquiry-header">
           
            <h1>Request a Service</h1>
            <p>Tell us what you need and we'll connect you with skilled professionals</p>
          </div>

          {message.text && (
            <div className={`inquiry-message ${message.type}`}>
              {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
              <span>{message.text}</span>
            </div>
          )}

          <div className="inquiry-progress">
            <div className={`progress-step ${currentStep >= 1 ? 'active' : ''}`}>
              <div className="step-number">1</div>
              <span>Service Details</span>
            </div>
            <div className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}>
              <div className="step-number">2</div>
              <span>Schedule & Location</span>
            </div>
            <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
              <div className="step-number">3</div>
              <span>Review & Submit</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="inquiry-form">
            {/* Step 1: Service Details */}
            {currentStep === 1 && (
              <div className="form-step">
                <h3>What service do you need?</h3>
                
                <div className="form-group">
                  <label htmlFor="title">Service Title *</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g., House cleaning, Plumbing repair, etc."
                    className={errors.title ? 'error' : ''}
                  />
                  {errors.title && <span className="error-text">{errors.title}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description *</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe what you need in detail..."
                    rows={4}
                    className={errors.description ? 'error' : ''}
                  />
                  {errors.description && <span className="error-text">{errors.description}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="categoryId">Category *</label>
                    <select
                      id="categoryId"
                      name="categoryId"
                      value={formData.categoryId}
                      onChange={handleInputChange}
                      className={errors.categoryId ? 'error' : ''}
                    >
                      <option value="">Select a category</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                      ))}
                    </select>
                    {errors.categoryId && <span className="error-text">{errors.categoryId}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="skillId">Specific Skill (Optional)</label>
                    <select
                      id="skillId"
                      name="skillId"
                      value={formData.skillId}
                      onChange={handleInputChange}
                      disabled={!selectedCategory}
                    >
                      <option value="">Select a skill</option>
                      {selectedCategory?.skills?.map(skill => (
                        <option key={skill.id} value={skill.id}>{skill.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={handleNext} className="next-button">
                    Next Step
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Schedule & Location */}
            {currentStep === 2 && (
              <div className="form-step">
                <h3>When and where do you need this service?</h3>
                
                <div className="form-group">
                  <label htmlFor="locationAddress">Location *</label>
                  <input
                    type="text"
                    id="locationAddress"
                    name="locationAddress"
                    value={formData.locationAddress}
                    onChange={handleInputChange}
                    placeholder="Enter the service location"
                    className={errors.locationAddress ? 'error' : ''}
                  />
                  {errors.locationAddress && <span className="error-text">{errors.locationAddress}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="scheduledDate">Preferred Date *</label>
                    <input
                      type="date"
                      id="scheduledDate"
                      name="scheduledDate"
                      value={formData.scheduledDate}
                      onChange={handleInputChange}
                      min={getTodayDate()}
                      className={errors.scheduledDate ? 'error' : ''}
                    />
                    {errors.scheduledDate && <span className="error-text">{errors.scheduledDate}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="scheduledTime">Preferred Time *</label>
                    <input
                      type="time"
                      id="scheduledTime"
                      name="scheduledTime"
                      value={formData.scheduledTime}
                      onChange={handleInputChange}
                      className={errors.scheduledTime ? 'error' : ''}
                    />
                    {errors.scheduledTime && <span className="error-text">{errors.scheduledTime}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="budgetMin">Minimum Budget (JMD)</label>
                    <input
                      type="number"
                      id="budgetMin"
                      name="budgetMin"
                      value={formData.budgetMin}
                      onChange={handleInputChange}
                      placeholder="0"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="budgetMax">Maximum Budget (JMD)</label>
                    <input
                      type="number"
                      id="budgetMax"
                      name="budgetMax"
                      value={formData.budgetMax}
                      onChange={handleInputChange}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="priority">Priority Level</label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                  >
                    <option value="low">Low - No rush</option>
                    <option value="medium">Medium - Standard</option>
                    <option value="high">High - Important</option>
                    <option value="urgent">Urgent - ASAP</option>
                  </select>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={() => setCurrentStep(1)} className="back-button-form">
                    Previous
                  </button>
                  <button type="button" onClick={handleNext} className="next-button">
                    Review Details
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Review & Submit */}
            {currentStep === 3 && (
              <div className="form-step">
                <h3>Review your service request</h3>
                
                <div className="review-section">
                  <div className="review-card">
                    <h4>Service Details</h4>
                    <div className="review-item">
                      <strong>Title:</strong> {formData.title}
                    </div>
                    <div className="review-item">
                      <strong>Category:</strong> {selectedCategory?.name}
                    </div>
                    {formData.skillId && (
                      <div className="review-item">
                        <strong>Skill:</strong> {selectedCategory?.skills.find(s => s.id === parseInt(formData.skillId))?.name}
                      </div>
                    )}
                    <div className="review-item">
                      <strong>Description:</strong> {formData.description}
                    </div>
                  </div>

                  <div className="review-card">
                    <h4>Schedule & Location</h4>
                    <div className="review-item">
                      <strong>Date:</strong> {new Date(formData.scheduledDate).toLocaleDateString()}
                    </div>
                    <div className="review-item">
                      <strong>Time:</strong> {formData.scheduledTime}
                    </div>
                    <div className="review-item">
                      <strong>Location:</strong> {formData.locationAddress}
                    </div>
                    {(formData.budgetMin || formData.budgetMax) && (
                      <div className="review-item">
                        <strong>Budget:</strong> 
                        {formData.budgetMin && formData.budgetMax 
                          ? `JMD $${formData.budgetMin} - $${formData.budgetMax}`
                          : formData.budgetMin 
                            ? `JMD $${formData.budgetMin}+`
                            : `Up to JMD $${formData.budgetMax}`
                        }
                      </div>
                    )}
                    <div className="review-item">
                      <strong>Priority:</strong> {formData.priority.charAt(0).toUpperCase() + formData.priority.slice(1)}
                    </div>
                  </div>

                  {availableWorkers.length > 0 && (
                    <div className="review-card">
                      <h4>Available Workers in Your Area</h4>
                      <div className="workers-list">
                        {availableWorkers.slice(0, 3).map(worker => (
                          <div key={worker.id} className="worker-preview">
                            <div className="worker-info">
                              <strong>{worker.first_name} {worker.last_name}</strong>
                              <div className="worker-meta">
                                <span className="rating">
                                  <Star size={14} />
                                  {worker.rating}/5 ({worker.total_reviews} reviews)
                                </span>
                                <span className="rate">JMD ${worker.hourly_rate}/hr</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="clientNotes">Additional Notes (Optional)</label>
                  <textarea
                    id="clientNotes"
                    name="clientNotes"
                    value={formData.clientNotes}
                    onChange={handleInputChange}
                    placeholder="Any additional information or special requirements..."
                    rows={3}
                  />
                </div>

                <div className="form-actions">
                  <button type="button" onClick={() => setCurrentStep(2)} className="back-button-form">
                    Previous
                  </button>
                  <button type="submit" disabled={isSubmitting} className="submit-button">
                    {isSubmitting ? (
                      <>
                        <div className="loading-spinner small" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Submit Request
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ServiceInquiry;